const STORAGE_PREFIX = 'sfilatoEditorialFeedback:v2:';
const READER_TOKEN_KEY = 'sfilatoEditorialReader:v1';

function readDeviceState(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key) || '{}');
  } catch (_error) {
    return {};
  }
}

function writeDeviceState(key, state) {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch (_error) {
    // Server persistence remains authoritative when browser storage is blocked.
  }
}

function createReaderToken() {
  const bytes = new Uint8Array(32);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
    .replace(/[^A-Za-z0-9_-]/g, '')
    .padEnd(32, '0')
    .slice(0, 64);
}

function readerToken() {
  try {
    const existing = window.localStorage.getItem(READER_TOKEN_KEY) || '';
    if (/^[A-Za-z0-9_-]{32,128}$/.test(existing)) return existing;
    const created = createReaderToken();
    window.localStorage.setItem(READER_TOKEN_KEY, created);
    return created;
  } catch (_error) {
    return createReaderToken();
  }
}

function groupElement(root, group) {
  return root.querySelector(`[data-feedback-group="${group}"]`);
}

function setGroupState(root, group, value) {
  const element = groupElement(root, group);
  if (!element) return;

  if (group === 'rating') {
    element.querySelectorAll('input[data-feedback-value]').forEach((input) => {
      input.checked = input.value === String(value);
    });
    return;
  }

  element.querySelectorAll('button[data-feedback-value]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.feedbackValue === String(value)));
  });
}

function setGroupBusy(root, group, busy) {
  const element = groupElement(root, group);
  if (!element) return;
  element.querySelectorAll('input, button').forEach((control) => {
    control.disabled = busy;
  });
  element.setAttribute('aria-busy', String(busy));
}

function setAllControlsDisabled(root, disabled) {
  root.querySelectorAll('[data-feedback-group] input, [data-feedback-group] button').forEach((control) => {
    control.disabled = disabled;
  });
}

function analyticsPayload(root, signal, value) {
  return {
    article_id: root.dataset.articleId,
    article_handle: root.dataset.articleHandle,
    signal,
    value,
    page_path: window.location.pathname,
  };
}

function storagePayload(root, signal, value, token) {
  return {
    article_id: root.dataset.articleId,
    article_handle: root.dataset.articleHandle,
    reader_token: token,
    signal,
    value,
  };
}

function analyticsProcessingAllowed() {
  const privacyApi = window.Shopify?.customerPrivacy;
  if (!privacyApi || typeof privacyApi.analyticsProcessingAllowed !== 'function') return false;

  try {
    return privacyApi.analyticsProcessingAllowed() === true;
  } catch (_error) {
    return false;
  }
}

function emitAnalytics(payload) {
  if (!analyticsProcessingAllowed()) return false;

  const eventName = 'article_feedback';
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: eventName, ...payload });
  }
  if (window.Shopify?.analytics?.publish) {
    try {
      const result = window.Shopify.analytics.publish(eventName, payload);
      result?.catch?.(() => {});
    } catch (_error) {
      // Analytics failure must never interrupt persisted reader feedback.
    }
  }
  try {
    window.dispatchEvent(new CustomEvent('sfilato:article-feedback', { detail: payload }));
  } catch (_error) {
    // CustomEvent is an optional integration surface in older browsers.
  }
  return true;
}

function sameOriginEndpoint(rawEndpoint) {
  if (!rawEndpoint || !rawEndpoint.startsWith('/') || rawEndpoint.startsWith('//')) return null;
  try {
    const endpoint = new URL(rawEndpoint, window.location.origin);
    return endpoint.origin === window.location.origin ? endpoint.href : null;
  } catch (_error) {
    return null;
  }
}

function summaryEndpoint(endpoint, root) {
  const url = new URL(endpoint);
  url.searchParams.set('article_id', root.dataset.articleId);
  url.searchParams.set('article_handle', root.dataset.articleHandle);
  return url.href;
}

function validSummary(payload) {
  return (
    payload &&
    payload.rating &&
    payload.useful &&
    (payload.rating.average === null || Number.isFinite(Number(payload.rating.average))) &&
    Number.isSafeInteger(Number(payload.rating.count)) &&
    Number.isSafeInteger(Number(payload.useful.yes)) &&
    Number.isSafeInteger(Number(payload.useful.total))
  );
}

function renderSummary(root, payload) {
  if (!validSummary(payload)) return false;
  const ratingCount = Number(payload.rating.count);
  const usefulYes = Number(payload.useful.yes);
  const usefulTotal = Number(payload.useful.total);
  const empty = root.querySelector('[data-feedback-rating-empty]');
  const populated = root.querySelector('[data-feedback-rating-populated]');
  const useful = root.querySelector('[data-feedback-useful-summary]');

  if (empty) empty.hidden = ratingCount > 0;
  if (populated) populated.hidden = ratingCount === 0;
  const average = root.querySelector('[data-feedback-rating-average]');
  const count = root.querySelector('[data-feedback-rating-count]');
  if (average && payload.rating.average !== null) {
    average.textContent = Number(payload.rating.average).toFixed(1);
  }
  if (count) count.textContent = String(ratingCount);

  if (useful) {
    useful.hidden = usefulTotal === 0;
    const yes = useful.querySelector('[data-feedback-useful-yes]');
    const total = useful.querySelector('[data-feedback-useful-total]');
    const percent = useful.querySelector('[data-feedback-useful-percent]');
    if (yes) yes.textContent = String(usefulYes);
    if (total) total.textContent = String(usefulTotal);
    if (percent) percent.textContent = String(Math.round((usefulYes / Math.max(1, usefulTotal)) * 100));
  }
  return true;
}

async function fetchSummary(endpoint, root) {
  const response = await window.fetch(summaryEndpoint(endpoint, root), {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('summary unavailable');
  const payload = await response.json();
  if (!renderSummary(root, payload)) throw new Error('summary invalid');
}

async function sendToEndpoint(endpoint, payload) {
  if (!endpoint) throw new Error('Feedback storage is not configured.');
  const response = await window.fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ version: 1, ...payload }),
    keepalive: true,
  });
  let responsePayload = {};
  try {
    responsePayload = await response.json();
  } catch (_error) {
    // The error below stays generic when the proxy did not return JSON.
  }
  if (!response.ok || responsePayload.accepted !== true) {
    throw new Error(responsePayload.error || 'Your feedback could not be saved. Please try again.');
  }
  return responsePayload;
}

function initialiseFeedback(root) {
  if (root.dataset.feedbackReady === 'true') return;
  root.dataset.feedbackReady = 'true';

  const status = root.querySelector('[data-feedback-status]');
  const storageKey = `${STORAGE_PREFIX}${root.dataset.articleId}`;
  const state = readDeviceState(storageKey);
  const endpoint = sameOriginEndpoint(root.dataset.feedbackEndpoint);
  const token = readerToken();
  const pendingSignals = new Set();

  if (state.rating) setGroupState(root, 'rating', state.rating);
  if (state.useful) setGroupState(root, 'useful', state.useful);
  if ((state.rating || state.useful) && status) {
    status.textContent = 'Your previous feedback is shown. You can update it.';
  }

  if (!endpoint) {
    setAllControlsDisabled(root, true);
    if (status) status.textContent = 'Reader feedback is temporarily unavailable.';
    return;
  }

  fetchSummary(endpoint, root).catch(() => {
    const empty = root.querySelector('[data-feedback-rating-empty]');
    if (empty) empty.textContent = 'The reader rating is temporarily unavailable.';
  });

  const submit = async (signal, value) => {
    if (pendingSignals.has(signal)) return;
    if (state[signal] === value) {
      if (status) status.textContent = 'That response is already saved. Choose another value to update it.';
      return;
    }

    pendingSignals.add(signal);
    const previous = state[signal];
    setGroupState(root, signal, value);
    setGroupBusy(root, signal, true);
    if (status) status.textContent = previous ? 'Updating your feedback…' : 'Saving your feedback…';

    try {
      const basePayload = analyticsPayload(root, signal, value);
      const result = await sendToEndpoint(endpoint, storagePayload(root, signal, value, token));
      state[signal] = value;
      writeDeviceState(storageKey, state);
      renderSummary(root, result);
      if (result.state === 'created' || result.state === 'updated') {
        emitAnalytics({ ...basePayload, persistence: 'server', response_state: result.state });
      }
      if (status) {
        status.textContent = result.state === 'updated'
          ? 'Thank you. Your feedback was updated.'
          : result.state === 'unchanged'
            ? 'That response was already saved.'
            : 'Thank you. Your feedback was saved.';
      }
    } catch (error) {
      if (previous) setGroupState(root, signal, previous);
      if (!previous && signal === 'rating') {
        groupElement(root, signal)?.querySelectorAll('input').forEach((input) => {
          input.checked = false;
        });
      }
      if (status) {
        status.textContent = error instanceof Error
          ? error.message
          : 'Your feedback could not be saved. Please try again.';
      }
    } finally {
      pendingSignals.delete(signal);
      setGroupBusy(root, signal, false);
    }
  };

  root.querySelectorAll('[data-feedback-group="rating"] input[data-feedback-value]').forEach((input) => {
    input.addEventListener('change', () => void submit('rating', Number(input.value)));
  });
  root.querySelectorAll('[data-feedback-group="useful"] button[data-feedback-value]').forEach((button) => {
    button.addEventListener('click', () => void submit('useful', button.dataset.feedbackValue));
  });
}

document.querySelectorAll('[data-editorial-feedback]').forEach(initialiseFeedback);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('[data-editorial-feedback]').forEach(initialiseFeedback);
});
